import { Heading, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { T_KEY } from "@/src/constants/translationsKey";

import { Locale } from "../../../utils/types";
import NoSSR from "../../NoSSR";
import Logo from "../Logo/Logo";

import styles from "./InfosWrapper.module.scss";

export function CompanyInfos() {
  const { t } = useTranslation(T_KEY);

  return (
    <>
      <Heading fontSize="sm" color="gray.700" mb="8px">
        {t("moais")}
      </Heading>
      <div className={styles.infos}>
        <div>
          <Text fontSize="sm" color="gray.600">
            {t("our_ceo")}
          </Text>
        </div>
        <div>
          <Text fontSize="sm" color="gray.600">
            {t("registration_number")}
          </Text>
        </div>
        <div>
          <Text fontSize="sm" color="gray.600">
            {t("company_address")}
          </Text>
        </div>

        <div>
          <Text fontSize="sm" color="gray.600">
            {t("cs_email")}
          </Text>
        </div>
      </div>
    </>
  );
}

export const infoLinks: Record<
  Locale,
  {
    tos: string;
    privacy: string;
    marketing: string;
    thirdparty: string;
  }
> = {
  ko: {
    tos: "https://www.moais.co.kr/golffix-terms-kr-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-kr-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-kr-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-kr-thirdparty",
  },
  en: {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  ja: {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  fr: {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  es: {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  "zh-CN": {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  "zh-TW": {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  de: {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  it: {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
  sv: {
    tos: "https://www.moais.co.kr/golffix-terms-en-tos",
    privacy: "https://www.moais.co.kr/golffix-terms-en-privacy",
    marketing: "https://www.moais.co.kr/golffix-terms-en-marketing",
    thirdparty: "https://www.moais.co.kr/golffix-terms-en-thirdparty",
  },
} as unknown as Record<
  Locale,
  {
    tos: string;
    privacy: string;
    marketing: string;
    thirdparty: string;
  }
>;

// www 브랜드 소개 페이지. gf-web 라우트 `/[lang]/golffix` 는 ALL_LOCALES(10개, BCP-47 하이픈)를
// 그대로 받으므로 web 의 Locale 값을 변환 없이 경로에 넣는다. 확장 locale(ko/en 외)은 www 쪽이
// UX 로만 서빙하고 noindex 처리한다(SD-8402) — 사용자는 자기 언어로 도착하고, 색인 신호는
// canonical 을 통해 en 으로 모인다.
export const brandSiteUrl = (locale: Locale): string =>
  `https://www.golffix.io/${locale}/golffix`;

interface InfoWrapperProps {
  locale: Locale;
}

export default function InfosWrapper({
  locale,
}: InfoWrapperProps): React.ReactElement {
  const { t } = useTranslation(T_KEY);

  // eslint-disable-next-line security/detect-object-injection -- locale은 Locale 리터럴 union 타입 prop, infoLinks는 내부 상수 맵
  const links = infoLinks[locale];

  return (
    <NoSSR>
      <div className={styles.infosWrapper} data-testid="infos-wrapper">
        <div className={styles.logoWrapper}>
          <Logo noLink className={styles.logo} alt="footer-logo" />
        </div>

        <CompanyInfos />

        <ul>
          <li>
            {/* SD-8523: web → www 브랜드 앵커 링크. 한글 "골프픽스" 엔티티를 www 로 결속시키기 위한
                외부 신호 — 앵커 텍스트는 로케일 브랜드명(ko: 골프픽스) 을 그대로 쓴다. */}
            <a href={brandSiteUrl(locale)}>{t("golffix")}</a>
          </li>
          <li>
            <a href={links?.tos}>{t("service_terms")}</a>
          </li>
          <li>
            <a href={links?.privacy}>{t("privacy_policy")}</a>
          </li>
          <li>
            <a href={links?.marketing}>{t("third_party_info")}</a>
          </li>
          <li>
            <a href={links?.thirdparty}>{t("marketing_info")}</a>
          </li>
        </ul>
      </div>
    </NoSSR>
  );
}
